import { plumeAPI } from "@/src/helpers/ServiceHelper";

/**
 * Fetches user's word count history from the backend
 * @param {string} token - JWT auth token
 * @param {Date} since - Date to fetch history from
 * @returns {Promise<Array>} Word count history
 */
export async function fetchWordCountHistory(token, since) {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const response = await plumeAPI.get(`/users_api/users/word_count_history?since=${since.toISOString()}`, config);
  return response.data;
}

/**
 * Fetches user's current writing streak from the backend. Computed and
 * persisted by scheduler's twice-daily job, not derived client-side.
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} Streak data
 */
export async function fetchStreak(token) {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const response = await plumeAPI.get("/users_api/users/streak", config);
  return response.data;
}

/**
 * Fetches user's writing goals from the backend
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} User goals
 */
export async function fetchUserGoals(token) {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const response = await plumeAPI.get("/writers_dashboard_api/goals", config);
  return response.data;
}

/**
 * Updates user's writing goals
 * @param {string} token - JWT auth token
 * @param {Object} goals - User goals object
 * @returns {Promise<Object>} Updated goals
 */
export async function updateUserGoals(token, goals) {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  const payload = {
    daily_goal: Math.max(0, parseInt(goals.daily_goal) || 0),
    weekly_goal: Math.max(0, parseInt(goals.weekly_goal) || 0),
    monthly_goal: Math.max(0, parseInt(goals.monthly_goal) || 0),
    writing_days: Array.isArray(goals.writing_days) ? goals.writing_days.join(",") : "",
  };

  const response = await plumeAPI.put("/writers_dashboard_api/goals", payload, config);
  return response.data;
}

/**
 * Updates a book's stats settings: { target_word_count } and/or
 * { counts_toward_stats }.
 * @param {string} token - JWT auth token
 * @param {number} bookId - Book ID
 * @param {Object} fields - the settings to change
 */
export async function updateBookStats(token, bookId, fields) {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
  const response = await plumeAPI.put("/writers_dashboard_api/goals/book", { book_id: bookId, ...fields }, config);
  return response.data;
}

/**
 * Fetches available writing challenges
 * @param {string} token - JWT auth token
 * @returns {Promise<Array>} Available challenges
 */
export async function fetchAvailableChallenges(token) {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const response = await plumeAPI.get("/writers_dashboard_api/challenges", config);
  return response.data;
}

/**
 * Fetches user's active challenges
 * @param {string} token - JWT auth token
 * @returns {Promise<Array>} User's active challenges
 */
export async function fetchUserChallenges(token) {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const response = await plumeAPI.get("/writers_dashboard_api/challenges/user", config);
  return response.data;
}

/**
 * Joins a writing challenge
 * @param {string} token - JWT auth token
 * @param {Object} challengeData - Challenge data
 * @returns {Promise<Object>} Joined challenge data
 */
export async function joinChallenge(token, challengeData) {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  const response = await plumeAPI.post("/writers_dashboard_api/challenges/user", challengeData, config);
  return response.data;
}

/**
 * Abandons a writing challenge
 * @param {string} token - JWT auth token
 * @param {number} challengeId - Challenge ID to abandon
 * @returns {Promise<Object>} Response data
 */
export async function abandonChallenge(token, challengeId) {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  const payload = {
    challenge_id: challengeId,
  };

  const response = await plumeAPI.put("/writers_dashboard_api/challenges/user", payload, config);
  return response.data;
}
